import {
  PRESSED_CHOICE_MAX_LENGTH,
  pressedChoiceLabelPlain,
  type RichReplyTappedCard,
} from "@cleo/shared/richReply";
import {
  type EnvironmentId,
  type MessageId,
  ProjectId,
  type ModelSelection,
  MANAGED_PROVIDER_INSTANCE_ID,
  type ProviderDriverKind,
  providerMidRunDelivery,
  type QueuedMessage,
  sendWhileRunning,
  type SendWhileRunning,
  type ServerProvider,
  type ScopedProjectRef,
  type ScopedThreadRef,
  type ThreadId,
  type TurnId,
  compactionQueueId,
  scheduledSendOf,
  scheduledSendQueueId,
} from "@cleo/contracts";
import {
  deriveBoundProviderKind,
  engineSwitchConfirmation,
  engineSwitchNeedsConfirmation,
  engineSwitchNotice,
  governedProviderDisabledReason,
  resolveProviderDriverKindForLock,
  sendSwitchesEngine,
} from "@cleo/shared/providerSwitchLaw";
import type { CreateThreadInput, StartThreadTurnInput } from "@cleo/client-runtime/operations";
import { type ChatMessage, type SessionPhase, type Thread, type ThreadShell } from "../types";
import {
  type ComposerImageAttachment,
  type ComposerThreadDraftState,
  type DraftThreadState,
  isPersonTypedDraft,
} from "../composerDraftStore";
import { formatConversationReferencesForSend } from "@cleo/shared/conversationReference";
import { applyClaudePromptEffortPrefix, resolvePromptInjectedEffort } from "@cleo/shared/model";
import { getProviderModelCapabilities } from "../providerModels";
import * as Schema from "effect/Schema";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { environmentThreadDetails } from "../state/threads";
import type { ComposerDictationStatus } from "../state/dictation";
import {
  filterTerminalContextsWithText,
  stripInlineTerminalContextPlaceholders,
  type TerminalContextDraft,
} from "../lib/terminalContext";
import type { DraftThreadEnvMode } from "../composerDraftStore";
import { formatProviderDriverKindLabel } from "../providerModels";

export const LAST_INVOKED_SCRIPT_BY_PROJECT_KEY = "cleo:last-invoked-script-by-project";
export const MAX_HIDDEN_MOUNTED_TERMINAL_THREADS = 10;
export const ENVIRONMENT_RECONNECT_WARNING_GRACE_MS = 2_000;

/**
 * Per-session timeline reading anchors are scalar records (thread key →
 * message id), not retained views; the cap only bounds a pathological number
 * of visited sessions in one long-lived instance.
 */
/**
 * Where "Forked from «…» at this message" goes (W-1efa94b1): the original's route, addressed to the message
 * the fork came from, so the original opens at that message rather than at its end.
 */
export function forkSourceNavigation(
  environmentId: EnvironmentId,
  threadId: ThreadId,
  messageId: MessageId,
) {
  return {
    to: "/$environmentId/$threadId" as const,
    params: { environmentId, threadId },
    search: { m: messageId },
  };
}

/** While an addressed message is not in the loaded window, earlier turns are loaded until it is. */
export function shouldLoadOlderTurnsForFocus(input: {
  readonly focusMessageId: string | null;
  readonly loadedMessageIds: ReadonlySet<string>;
  readonly hasOlderTurns: boolean;
  readonly loadingOlder: boolean;
}): boolean {
  return (
    input.focusMessageId !== null &&
    !input.loadedMessageIds.has(input.focusMessageId) &&
    input.hasOlderTurns &&
    !input.loadingOlder
  );
}

export const MAX_TIMELINE_READING_ANCHOR_THREADS = 50;

/**
 * Records where the reader left a session's timeline. A null message id means
 * the session was at the live end, which erases the record so the next visit
 * resumes following the end (A2 timeline-continuity law).
 */
export function recordTimelineReadingAnchor(
  anchorsByThreadKey: Map<string, string>,
  threadKey: string,
  messageId: string | null,
  maxEntries: number = MAX_TIMELINE_READING_ANCHOR_THREADS,
): void {
  if (messageId === null) {
    anchorsByThreadKey.delete(threadKey);
    return;
  }
  anchorsByThreadKey.delete(threadKey);
  anchorsByThreadKey.set(threadKey, messageId);
  while (anchorsByThreadKey.size > maxEntries) {
    const oldest = anchorsByThreadKey.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    anchorsByThreadKey.delete(oldest);
  }
}

export const LOCAL_DRAFT_SLICE_BINDING_ERROR = {
  title: "Start governed work from its Session",
  description:
    "This project draft is Local and was not sent into the active World. Open the intended Session and start its execution there.",
} as const;

export const SOURCE_THREAD_SLICE_BINDING_ERROR = {
  title: "Could not start implementation thread",
  description:
    "The source thread has an incomplete World Session binding, so its implementation thread was not created.",
} as const;

export const LastInvokedScriptByProjectSchema = Schema.Record(ProjectId, Schema.String);

export function scheduleEnvironmentReconnectWarning(showWarning: () => void): () => void {
  const timeoutId = globalThis.setTimeout(showWarning, ENVIRONMENT_RECONNECT_WARNING_GRACE_MS);
  return () => globalThis.clearTimeout(timeoutId);
}

export function hasEnvironmentReconnectWarningGraceElapsed(
  activeEnvironmentId: EnvironmentId | null,
  elapsedEnvironmentId: EnvironmentId | null,
): boolean {
  return activeEnvironmentId !== null && activeEnvironmentId === elapsedEnvironmentId;
}

export function startNewThreadForProject(
  projectRef: ScopedProjectRef | null,
  handleNewThread: (projectRef: ScopedProjectRef) => Promise<unknown>,
): boolean {
  if (projectRef === null) return false;
  void handleNewThread(projectRef);

  return true;
}

export function prepareLocalDraftTurnStart(
  activeWorldKey: string | null,
  input: StartThreadTurnInput,
):
  | { readonly status: "ready"; readonly input: StartThreadTurnInput }
  | { readonly status: "blocked"; readonly error: typeof LOCAL_DRAFT_SLICE_BINDING_ERROR } {
  if (activeWorldKey !== null) {
    return { status: "blocked", error: LOCAL_DRAFT_SLICE_BINDING_ERROR };
  }

  const createThread = input.bootstrap?.createThread;
  if (!createThread) {
    return { status: "blocked", error: LOCAL_DRAFT_SLICE_BINDING_ERROR };
  }

  // Signed-out Local is the traditional harness: it deliberately carries no
  // invented World. Governed execution creation belongs to the Session surface.
  return { status: "ready", input };
}

export function preparePlanImplementationThreadCreate(
  sourceThread: Pick<Thread, "worldKey" | "sessionId"> & Partial<Pick<Thread, "id">>,
  input: CreateThreadInput,
):
  | { readonly status: "ready"; readonly input: CreateThreadInput }
  | {
      readonly status: "blocked";
      readonly error: typeof SOURCE_THREAD_SLICE_BINDING_ERROR;
    } {
  const { worldKey, sessionId } = sourceThread;
  if (worldKey == null && sessionId == null) {
    return {
      status: "ready",
      input: { ...input, ...(sourceThread.id ? { parentThreadId: sourceThread.id } : {}) },
    };
  }
  if (worldKey == null || sessionId == null) {
    return { status: "blocked", error: SOURCE_THREAD_SLICE_BINDING_ERROR };
  }

  return {
    status: "ready",
    input: {
      ...input,
      worldKey,
      sessionId,
    },
  };
}

// Can 21 (2026-10-02): in a started conversation, a Send naming another
// engine carries the switch itself (the server records thread.engine-switched
// and gives the new engine the conversation so far). The pre-write must not
// move the binding first: the decider refuses a thread.meta.update that names
// another driver, and a same-driver cross-instance pre-write would move the
// engine without the carry. So the pre-write omits `modelSelection` when the
// thread has started and the instance changed. Model and option changes on the
// same instance keep pre-writing.
export function resolveThreadMetadataUpdateForNextTurn(input: {
  currentModelSelection: ModelSelection;
  nextModelSelection?: ModelSelection;
  currentBranch: string | null;
  nextBranch?: string;
  /** True once the thread has any turn, message or session (threadHasStarted). */
  threadStarted?: boolean;
}): {
  modelSelection?: ModelSelection;
  branch?: string;
  worktreePath?: null;
} | null {
  const nextModelSelection = input.nextModelSelection;
  const modelSelectionChanged =
    nextModelSelection !== undefined &&
    (nextModelSelection.model !== input.currentModelSelection.model ||
      nextModelSelection.instanceId !== input.currentModelSelection.instanceId ||
      JSON.stringify(nextModelSelection.options ?? null) !==
        JSON.stringify(input.currentModelSelection.options ?? null));
  const sendCarriesSwitch =
    modelSelectionChanged &&
    input.threadStarted === true &&
    nextModelSelection.instanceId !== input.currentModelSelection.instanceId;
  const writeModelSelection = modelSelectionChanged && !sendCarriesSwitch;
  const branchChanged = input.nextBranch !== undefined && input.nextBranch !== input.currentBranch;
  if (!writeModelSelection && !branchChanged) {
    return null;
  }
  return {
    ...(writeModelSelection ? { modelSelection: nextModelSelection } : {}),
    ...(branchChanged ? { branch: input.nextBranch, worktreePath: null } : {}),
  };
}

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
  fallbackModelSelection: ModelSelection,
): Thread {
  return {
    id: threadId,
    environmentId: draftThread.environmentId,
    projectId: draftThread.projectId,
    title: "New thread",
    modelSelection: fallbackModelSelection,
    runtimeMode: draftThread.runtimeMode,
    interactionMode: draftThread.interactionMode,
    session: null,
    messages: [],
    createdAt: draftThread.createdAt,
    updatedAt: draftThread.createdAt,
    archivedAt: null,
    settledOverride: null,
    settledAt: null,
    deletedAt: null,
    latestTurn: null,
    branch: draftThread.branch,
    worktreePath: draftThread.worktreePath,
    checkpoints: [],
    activities: [],
    proposedPlans: [],
  };
}

export function buildLoadingThreadFromShell(shell: ThreadShell): Thread {
  return {
    ...shell,
    messages: [],
    proposedPlans: [],
    activities: [],
    checkpoints: [],
    deletedAt: null,
  };
}

export function shouldWriteThreadErrorToCurrentServerThread(input: {
  activeServerThread:
    | {
        environmentId: EnvironmentId;
        id: ThreadId;
      }
    | null
    | undefined;
  routeThreadRef: ScopedThreadRef;
  targetThreadId: ThreadId;
}): boolean {
  return Boolean(
    input.activeServerThread &&
    input.targetThreadId === input.routeThreadRef.threadId &&
    input.activeServerThread.environmentId === input.routeThreadRef.environmentId &&
    input.activeServerThread.id === input.targetThreadId,
  );
}

export function buildThreadTurnInterruptInput(thread: Pick<Thread, "id" | "session">): {
  threadId: ThreadId;
  turnId?: TurnId;
} {
  const runningTurnId = thread.session?.status === "running" ? thread.session.activeTurnId : null;
  return {
    threadId: thread.id,
    ...(runningTurnId !== null ? { turnId: runningTurnId } : {}),
  };
}

/**
 * Same terminal ids (order ignored) — avoids reconcile when only server
 * session ordering differs. Moved from ChatView.tsx as a CH1
 * characterization seam: the law is pinned before the CH2 extraction.
 */
export function terminalIdListsEqual(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  if (left.length === 0) {
    return true;
  }
  const sortedLeft = left.toSorted((a, b) => a.localeCompare(b));
  const sortedRight = right.toSorted((a, b) => a.localeCompare(b));
  for (let index = 0; index < sortedLeft.length; index += 1) {
    if (sortedLeft[index] !== sortedRight[index]) {
      return false;
    }
  }
  return true;
}

/**
 * Server knows about fewer sessions than the client, but every server id
 * still exists locally. Typical right after `terminal.open`: the
 * known-session list lags; reconciling would drop the new id and later
 * re-add it as a separate group (no split layout). Moved from ChatView.tsx
 * as a CH1 characterization seam.
 */
export function serverTerminalIdsStrictSubsetOfClient(
  serverIds: readonly string[],
  clientIds: readonly string[],
): boolean {
  if (serverIds.length >= clientIds.length || clientIds.length === 0) {
    return false;
  }
  const clientSet = new Set(clientIds);
  for (const id of serverIds) {
    if (!clientSet.has(id)) {
      return false;
    }
  }
  return true;
}

/**
 * The known-terminal union (CH1 seam): server-ordered ids first, then any
 * client-only ids the UI store still tracks, each id exactly once. Server
 * order wins so the tab strip matches the authority's ordering while a
 * just-opened local terminal stays visible before the server learns it.
 */
export function mergeKnownTerminalIds(
  serverOrderedIds: readonly string[],
  uiStateIds: readonly string[],
): string[] {
  return [...new Set([...serverOrderedIds, ...uiStateIds])];
}

export function reconcileMountedTerminalThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadTerminalOpen: boolean;
  maxHiddenThreadCount?: number;
}): string[] {
  return reconcileRetainedMountedThreadIds({
    currentThreadIds: input.currentThreadIds,
    openThreadIds: input.openThreadIds,
    activeThreadId: input.activeThreadId,
    activeThreadOpen: input.activeThreadTerminalOpen,
    maxHiddenThreadCount: input.maxHiddenThreadCount ?? MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
  });
}

function reconcileRetainedMountedThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadOpen: boolean;
  maxHiddenThreadCount: number;
  retainInactiveActiveThread?: boolean;
}): string[] {
  const openThreadIdSet = new Set(input.openThreadIds);
  const hiddenThreadIds = input.currentThreadIds.filter(
    (threadId) =>
      (threadId !== input.activeThreadId || input.retainInactiveActiveThread === true) &&
      openThreadIdSet.has(threadId),
  );
  const maxHiddenThreadCount = Math.max(0, input.maxHiddenThreadCount);
  const nextThreadIds =
    hiddenThreadIds.length > maxHiddenThreadCount
      ? hiddenThreadIds.slice(-maxHiddenThreadCount)
      : hiddenThreadIds;

  if (
    input.activeThreadId &&
    input.activeThreadOpen &&
    !nextThreadIds.includes(input.activeThreadId)
  ) {
    nextThreadIds.push(input.activeThreadId);
  }

  return nextThreadIds;
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  URL.revokeObjectURL(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") {
      continue;
    }
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

export function collectUserMessageBlobPreviewUrls(message: ChatMessage): string[] {
  if (message.role !== "user" || !message.attachments) {
    return [];
  }
  const previewUrls: string[] = [];
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") continue;
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrls.push(attachment.previewUrl);
  }
  return previewUrls;
}

/**
 * MQ2: a Send the durable queue took is shown by the queue's own bubble under
 * Cleo's working row (Unread, Paused, Not sent), or is gone because the person
 * removed it. Its optimistic row would show the same text a second time. The
 * entry keeps the Send's identity in its queue id after a pause drops the
 * frozen Send, so both are read.
 */
export function optimisticMessageIdsInQueue(
  optimisticMessages: ReadonlyArray<{ readonly id: string }>,
  queue: ReadonlyArray<Pick<QueuedMessage, "queueId" | "scheduledCommand">>,
): ReadonlySet<string> {
  if (optimisticMessages.length === 0 || queue.length === 0) return NO_MESSAGE_IDS;
  const queueIds = new Set(queue.map((item) => item.queueId));
  const frozenMessageIds = new Set(queue.map((item) => scheduledSendOf(item)?.messageId));
  const inQueue = optimisticMessages
    .map((message) => message.id)
    .filter(
      (id) =>
        frozenMessageIds.has(id) ||
        queueIds.has(scheduledSendQueueId(id)) ||
        queueIds.has(compactionQueueId(id)),
    );
  // One shared empty set, so the usual answer does not look like a change.
  return inQueue.length === 0 ? NO_MESSAGE_IDS : new Set(inQueue);
}
const NO_MESSAGE_IDS: ReadonlySet<string> = new Set();

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Could not read image data."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read image."));
    });
    reader.readAsDataURL(file);
  });
}

export function resolveSendEnvMode(input: {
  requestedEnvMode: DraftThreadEnvMode;
  isGitRepo: boolean;
}): DraftThreadEnvMode {
  return input.isGitRepo ? input.requestedEnvMode : "local";
}

export function cloneComposerImageForRetry(
  image: ComposerImageAttachment,
): ComposerImageAttachment {
  if (typeof URL === "undefined" || !image.previewUrl.startsWith("blob:")) {
    return image;
  }
  try {
    return {
      ...image,
      previewUrl: URL.createObjectURL(image.file),
    };
  } catch {
    return image;
  }
}

export function deriveComposerSendState(options: {
  prompt: string;
  imageCount: number;
  terminalContexts: ReadonlyArray<TerminalContextDraft>;
  /**
   * Optional element-pick attachment count. Element contexts contribute to
   * "sendable content" exactly like images and (text-bearing) terminal
   * contexts do: a prompt of just element chips is still a valid send.
   */
  elementContextCount?: number;
}): {
  trimmedPrompt: string;
  sendableTerminalContexts: TerminalContextDraft[];
  expiredTerminalContextCount: number;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = stripInlineTerminalContextPlaceholders(options.prompt).trim();
  const sendableTerminalContexts = filterTerminalContextsWithText(options.terminalContexts);
  const expiredTerminalContextCount =
    options.terminalContexts.length - sendableTerminalContexts.length;
  const elementContextCount = options.elementContextCount ?? 0;
  return {
    trimmedPrompt,
    sendableTerminalContexts,
    expiredTerminalContextCount,
    hasSendableContent:
      trimmedPrompt.length > 0 ||
      options.imageCount > 0 ||
      sendableTerminalContexts.length > 0 ||
      elementContextCount > 0,
  };
}

/**
 * What Send does on this thread while Cleo works: the person's "Queue messages
 * while Cleo works" preference, and what the engine running the response
 * declares it can do with a message handed to it mid-run.
 */
export function resolveRunningSend(input: {
  queueing: boolean;
  thread: Pick<Thread, "session" | "modelSelection"> | null | undefined;
  providers: ReadonlyArray<
    Pick<ServerProvider, "instanceId" | "midRunDelivery" | "midRunAttachments">
  >;
  /** The engine the composer's draft names, when the person picked one. */
  draftInstanceId?: string | null;
}): SendWhileRunning {
  const instanceId =
    input.thread?.session?.providerInstanceId ?? input.thread?.modelSelection.instanceId;
  const midRun = providerMidRunDelivery(
    input.providers.find((provider) => provider.instanceId === instanceId),
  );
  // Can 21 (2026-10-02): a Send naming another engine is a switch, and a
  // switch never joins the running response. It waits as Unread and goes to
  // the new engine after this response, whatever the queueing preference.
  // Callers pass a draft instance only on a started conversation.
  const switchesEngine = sendSwitchesEngine({
    threadStarted: input.draftInstanceId != null,
    boundInstanceId: instanceId,
    targetInstanceId: input.draftInstanceId,
  });
  return sendWhileRunning({
    queueing: input.queueing || switchesEngine,
    midRunDelivery: midRun.delivery,
    midRunAttachments: midRun.attachments,
  });
}

/**
 * The delivery a composer Send carries. Only a Send made while a response is
 * running or starting has one; an annotation, a Talk turn and an answer to
 * Cleo's question are never queued or steered.
 */
export function resolveComposerQueueDelivery(input: {
  phase: SessionPhase;
  hasDirectAnnotation: boolean;
  talkOn: boolean;
  answeringPendingQuestion: boolean;
  runningSend: Pick<SendWhileRunning, "delivery">;
}): SendWhileRunning["delivery"] | undefined {
  if (input.hasDirectAnnotation || input.talkOn || input.answeringPendingQuestion) return undefined;
  // W-a270e02c: a Send while the session is still starting has no run to steer
  // and no turn to wait for yet. It waits as its own Unread message and goes
  // when the response ends, whatever the queueing preference; sent as a plain
  // start it would reach the provider and be refused there.
  if (input.phase === "connecting") return "after-response";
  if (input.phase !== "running") return undefined;
  return input.runningSend.delivery;
}

/**
 * Why a choice clicked in Cleo's card cannot be sent now, or null when it can.
 * The click is an ordinary Send of the choice's words, so it is refused
 * wherever that Send would not go straight out as a new message: it never
 * queues behind a running response, never answers a pending question or plan
 * and never replaces or joins what the person has in the composer.
 */
export function resolveChoiceSendRefusal(input: {
  conversationKey: string;
  routeThreadKey: string;
  hasActiveThread: boolean;
  sendDisabledReason: string | null;
  sendRecoveryReady: boolean;
  /** The recovery that settled is the one for this conversation and draft. */
  sendRecoveryCurrent: boolean;
  environmentUnavailable: boolean;
  environmentUnavailableLabel: string | null;
  phase: SessionPhase;
  isSendBusy: boolean;
  sendInFlight: boolean;
  pendingApprovalCount: number;
  hasActivePendingProgress: boolean;
  pendingUserInputCount: number;
  showPlanFollowUpPrompt: boolean;
  dictationStatus: ComposerDictationStatus;
  providerAvailable: boolean;
  draftHasUserContent: boolean;
  prompt: string;
  fileAttachmentCount: number;
  /** Saved Sends of this conversation not yet confirmed by the server. */
  unconfirmedSendCount: number;
  /** The words the choice would send. */
  text: string;
}): string | null {
  if (input.conversationKey !== input.routeThreadKey || !input.hasActiveThread)
    return "Not sent: this conversation is no longer open.";
  if (input.sendDisabledReason) return `Not sent: ${input.sendDisabledReason}.`;
  if (!input.sendRecoveryReady || !input.sendRecoveryCurrent)
    return "Not sent: Restoring saved Send.";
  if (input.environmentUnavailable)
    return `Not sent: not connected to ${input.environmentUnavailableLabel ?? "this computer"}. Choose again once it is connected.`;
  // A Send while a response runs or starts would be queued after it (MQ2).
  if (input.phase === "running" || input.phase === "connecting")
    return "Not sent: Cleo is still working. Choose again when she has finished.";
  if (input.isSendBusy || input.sendInFlight)
    return "Not sent: your last message is still sending. Choose again in a moment.";
  // A saved Send whose outcome is unknown would be replayed by the same words,
  // with its own delivery (queued after the response).
  if (input.unconfirmedSendCount > 0)
    return "Not sent: a message you sent earlier is still being confirmed. Choose again in a moment.";
  if (input.pendingApprovalCount > 0)
    return "Not sent: Cleo is waiting for your approval. Answer it first.";
  if (input.hasActivePendingProgress || input.pendingUserInputCount > 0)
    return "Not sent: Cleo is waiting for your answer to her question. Answer it first.";
  if (input.showPlanFollowUpPrompt)
    return "Not sent: Cleo's plan is waiting for your reply in the composer.";
  if (input.dictationStatus !== "idle") return "Not sent: finish dictating first.";
  if (!input.providerAvailable)
    return "Not sent: no engine is available. Choose one in the composer.";
  if (input.draftHasUserContent || input.prompt.trim().length > 0 || input.fileAttachmentCount > 0)
    return "Not sent: your composer has a draft. Send or clear it, then choose again.";
  // The composer reads a leading "/" as a command (/plan switches the mode).
  if (input.text.trimStart().startsWith("/"))
    return 'Not sent: this choice starts with "/", which the composer reads as a command. Type it in your reply instead.';
  return null;
}

export function buildExpiredTerminalContextToastCopy(
  expiredTerminalContextCount: number,
  variant: "omitted" | "empty",
): { title: string; description: string } {
  const count = Math.max(1, Math.floor(expiredTerminalContextCount));
  const noun = count === 1 ? "Expired terminal context" : "Expired terminal contexts";
  if (variant === "empty") {
    return {
      title: `${noun} won't be sent`,
      description: "Remove it or re-add it to include terminal output.",
    };
  }
  return {
    title: `${noun} omitted from message`,
    description: "Re-add it if you want that terminal output included.",
  };
}

export function branchMismatchKey(
  threadId: string | null,
  mismatch: { threadBranch: string; currentBranch: string } | null,
): string | null {
  if (!threadId || !mismatch) {
    return null;
  }
  return `${threadId}:${mismatch.threadBranch}:${mismatch.currentBranch}`;
}

// The mismatch banner only matters when the user is about to send: passive
// reading of an old thread carries no risk (the branch picker tint already
// covers ambient awareness). Draft content is the intent signal — composer
// focus is useless here because ChatView autofocuses the composer on every
// thread open. `wasShownForCurrentMismatch` keeps the banner mounted once
// revealed so it doesn't flicker away when the draft is cleared.
export function shouldShowBranchMismatchBanner(input: {
  hasMismatch: boolean;
  isDismissed: boolean;
  composerHasContent: boolean;
  wasShownForCurrentMismatch: boolean;
}): boolean {
  if (!input.hasMismatch || input.isDismissed) {
    return false;
  }
  return input.composerHasContent || input.wasShownForCurrentMismatch;
}

// Session-scoped (module-level so it survives ChatView remounts, e.g. route
// changes). Durable cross-device dismissal is planned as a server-side ack.
const sessionDismissedBranchMismatchKeys = new Set<string>();

export function dismissBranchMismatchForSession(key: string): void {
  sessionDismissedBranchMismatchKeys.add(key);
}

export function isBranchMismatchDismissedForSession(key: string | null): boolean {
  return key !== null && sessionDismissedBranchMismatchKeys.has(key);
}

export function threadHasStarted(thread: Thread | null | undefined): boolean {
  return Boolean(
    thread && (thread.latestTurn !== null || thread.messages.length > 0 || thread.session !== null),
  );
}

// The bound-engine and governed-greying laws live in @cleo/shared/providerSwitchLaw
// (M17, 2026-08-29): mobile teaches the same law from the same functions, so
// neither client can drift sentence by sentence. These wrappers keep the
// web-local Thread shape. Since Can 21 (2026-10-02) the bound engine names
// what a started conversation runs on; it no longer locks the picker.
export function deriveBoundProvider(input: {
  thread: Thread | null | undefined;
  selectedProvider: string | null;
  threadProvider: string | null;
  providerCatalog: ReadonlyArray<{
    readonly instanceId: string;
    readonly driver: string;
  }>;
}): ProviderDriverKind | null {
  return deriveBoundProviderKind({
    threadStarted: threadHasStarted(input.thread),
    sessionProviderName: input.thread?.session?.providerName ?? null,
    threadProvider: resolveProviderDriverKindForLock({
      providerCatalog: input.providerCatalog,
      selection: input.threadProvider,
    }),
    selectedProvider: resolveProviderDriverKindForLock({
      providerCatalog: input.providerCatalog,
      selection: input.selectedProvider,
    }),
  });
}

// A governed thread records every send's frozen boundary through its driver's
// grant harness before the provider runs the turn; drivers with no harness
// grey with the shared teaching sentence — deliberately fail-closed (O19).
// The harness mapping stays in @cleo/contracts and the sentence in
// @cleo/shared; never re-list the governed driver kinds here.
export function getGovernedProviderDisabledReason(input: {
  /** True when the composer's next send will carry a contextResolution. */
  governed: boolean;
  driverKind: ProviderDriverKind;
  /** Presentation label for the disabled surface: group label or instance/provider display name. */
  label: string;
}): string | null {
  return governedProviderDisabledReason(input);
}

// Can 21 (2026-10-02): what was a block ("Start a new chat to change
// models") is now the switch notice. In a started conversation, choosing
// another instance hands the conversation so far to the engine with the next
// Send.
export function getStartedThreadModelChangeNotice(input: {
  threadStarted: boolean;
  currentModelSelection: ModelSelection;
  currentProviderInstanceId?: ModelSelection["instanceId"] | null | undefined;
  nextModelSelection: ModelSelection;
  /** The label of the engine chosen, as the picker shows it. */
  nextLabel: string;
}): string | null {
  if (!input.threadStarted) {
    return null;
  }
  const currentInstanceId =
    input.currentProviderInstanceId ?? input.currentModelSelection.instanceId;
  return currentInstanceId !== input.nextModelSelection.instanceId
    ? engineSwitchNotice(input.nextLabel)
    : null;
}

/** The engine a started conversation runs on: its session's instance, else its stored selection. */
export function boundInstanceIdOf(
  thread: Pick<Thread, "session" | "modelSelection"> | null | undefined,
): string | null {
  return thread?.session?.providerInstanceId ?? thread?.modelSelection.instanceId ?? null;
}

/**
 * Can 21 (2026-10-02): the confirmation a Send asks for before it moves a
 * started conversation to another engine, or null when it needs none. The
 * rule and the sentences are the shared law's (engineSwitchNeedsConfirmation,
 * engineSwitchConfirmation), so Pocket asks the same thing in the same words.
 */
export function resolveSendEngineSwitchConfirmation(input: {
  threadStarted: boolean;
  boundInstanceId: string | null;
  targetInstanceId: string;
  providers: ReadonlyArray<Pick<ServerProvider, "instanceId" | "driver" | "displayName">>;
}): ReturnType<typeof engineSwitchConfirmation> | null {
  const find = (instanceId: string | null) =>
    instanceId === null
      ? undefined
      : input.providers.find((provider) => provider.instanceId === instanceId);
  const bound = find(input.boundInstanceId);
  const target = find(input.targetInstanceId);
  const needs = engineSwitchNeedsConfirmation({
    threadStarted: input.threadStarted,
    bound:
      input.boundInstanceId === null
        ? null
        : { instanceId: input.boundInstanceId, driverKind: bound?.driver ?? null },
    target: { instanceId: input.targetInstanceId, driverKind: target?.driver ?? null },
  });
  if (!needs) return null;
  const labelOf = (
    provider: Pick<ServerProvider, "driver" | "displayName"> | undefined,
    fallback: string,
  ) =>
    provider?.displayName ?? (provider ? formatProviderDriverKindLabel(provider.driver) : fallback);
  return engineSwitchConfirmation({
    from: labelOf(bound, input.boundInstanceId ?? ""),
    to: labelOf(target, input.targetInstanceId),
    toDriverKind: target?.driver ?? null,
    managed: input.targetInstanceId === MANAGED_PROVIDER_INSTANCE_ID,
  });
}

export async function waitForStartedServerThread(
  threadRef: ScopedThreadRef,
  timeoutMs = 1_000,
): Promise<boolean> {
  const threadAtom = environmentThreadDetails.detailAtom(threadRef);
  const getThread = () => appAtomRegistry.get(threadAtom);
  const thread = getThread();

  if (threadHasStarted(thread)) {
    return true;
  }

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    const finish = (result: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      unsubscribe();
      resolve(result);
    };

    const unsubscribe = appAtomRegistry.subscribe(threadAtom, (thread) => {
      if (!threadHasStarted(thread)) {
        return;
      }
      finish(true);
    });

    if (threadHasStarted(getThread())) {
      finish(true);
      return;
    }

    timeoutId = globalThis.setTimeout(() => {
      finish(false);
    }, timeoutMs);
  });
}

export interface LocalDispatchSnapshot {
  startedAt: string;
  preparingWorktree: boolean;
  /** The conversation whose Send this is; its composer alone shows "Sending…" (W-829b2cff). */
  threadId: ThreadId | null;
  /** The Send carries what the composer holds (not a card's choice), so the composer waits for it. */
  holdsComposer: boolean;
  latestUserMessageId: ChatMessage["id"] | null;
  latestTurnTurnId: TurnId | null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionStatus: NonNullable<Thread["session"]>["status"] | null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | undefined,
  options?: { preparingWorktree?: boolean; holdsComposer?: boolean },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  const latestUserMessage = activeThread?.messages.findLast((message) => message.role === "user");
  return {
    startedAt: new Date().toISOString(),
    preparingWorktree: Boolean(options?.preparingWorktree),
    threadId: activeThread?.id ?? null,
    holdsComposer: Boolean(options?.holdsComposer),
    latestUserMessageId: latestUserMessage?.id ?? null,
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionStatus: session?.status ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

/**
 * W-829b2cff: a composer shows "Sending…" and takes no input only while a Send
 * of its own words is in flight, and only in the conversation that sent them:
 * a Send begun in A and finishing after a switch never holds B's composer.
 */
export function composerHeldBySend(
  activeLocalDispatch: LocalDispatchSnapshot | null,
  activeThreadId: ThreadId | null,
): boolean {
  return (
    activeLocalDispatch?.holdsComposer === true && activeLocalDispatch.threadId === activeThreadId
  );
}

export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  latestUserMessageId: ChatMessage["id"] | null;
  session: Thread["session"] | null;
  hasPendingApproval: boolean;
  hasPendingUserInput: boolean;
  threadError: string | null | undefined;
}): boolean {
  if (!input.localDispatch) {
    return false;
  }
  if (input.hasPendingApproval || input.hasPendingUserInput || Boolean(input.threadError)) {
    return true;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const latestUserMessageChanged =
    input.localDispatch.latestUserMessageId !== input.latestUserMessageId;
  const latestTurnChanged =
    input.localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    input.localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    input.localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    input.localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  if (input.phase === "running") {
    // Steering adds a user message to the current running turn without
    // necessarily changing any of the turn timestamps. Treat that projected
    // message as the server acknowledgment so the composer does not remain
    // stuck in its local "Sending" state until the turn settles.
    if (latestUserMessageChanged) {
      return true;
    }
    if (!latestTurnChanged) {
      return false;
    }
    if (latestTurn?.startedAt === null || latestTurn === null) {
      return false;
    }
    if (
      session?.activeTurnId !== null &&
      session?.activeTurnId !== undefined &&
      latestTurn?.turnId !== session.activeTurnId
    ) {
      return false;
    }
    return true;
  }

  return (
    latestTurnChanged ||
    input.localDispatch.sessionStatus !== (session?.status ?? null) ||
    input.localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
  );
}

/**
 * W-89a78759 (PR #1708): whether a composer Send's message text is exactly what
 * the person typed, so the Send may claim `composerTyped` (the server stamps the
 * person-authored mark only for the person's own Desk session). Only when the
 * person pressed Send on a draft whose every word came from their own input (no
 * app-inserted text since it was last empty). Never for a Send code started, a
 * card's choice, the attachments sent after an answer, a Talk-mode Send, or any
 * Send whose text gained context, a stand-in, a conversation reference or an
 * effort prefix: the text must equal the typed words, trimmed as the server
 * trims what it attests.
 */
/**
 * W-89a78759 (PR #1708): the draft's provenance at Send, for the exact text being
 * sent: every write since the draft was last empty came from the person's own input.
 */
export function draftTypedByPersonAtSend(
  draft: ComposerThreadDraftState | null | undefined,
  promptForSend: string,
): boolean {
  return isPersonTypedDraft(draft) && draft?.prompt === promptForSend;
}

export function isComposerTypedSend(input: {
  readonly typedPrompt: string;
  readonly outgoingText: string;
  /** The draft's provenance: every write since it was empty came from the person's own input. */
  readonly draftTypedByPerson: boolean;
  /** The Send was started by code (a resend after sign-in, a deferred or hands-free Send, a card's choice). */
  readonly programmatic: boolean;
  readonly fromCard: boolean;
  readonly answeredWithAttachments: boolean;
  readonly talkOn: boolean;
}): boolean {
  if (!input.draftTypedByPerson || input.programmatic) return false;
  if (input.fromCard || input.answeredWithAttachments || input.talkOn) return false;
  const typed = input.typedPrompt.trim();
  return typed.length > 0 && input.outgoingText.trim() === typed;
}

export function formatOutgoingPrompt(params: {
  provider: ProviderDriverKind;
  model: string | null;
  models: ReadonlyArray<ServerProvider["models"][number]>;
  effort: string | null;
  text: string;
}): string {
  const caps = getProviderModelCapabilities(params.models, params.model, params.provider);
  const promptEffort = resolvePromptInjectedEffort(caps, params.effort);
  // Cleo Can 53: a named conversation travels as a marker and one bounded block.
  return applyClaudePromptEffortPrefix(
    formatConversationReferencesForSend(params.text),
    promptEffort,
  );
}

/**
 * The pressed-choice law (the Orchestrator, 9 Oct 2026): the Send's claim that it is a choice the person pressed on
 * Cleo's card, or nothing. Only when the press was the person's own (a trusted click: `card.pressed`), nothing was
 * attached, the label is short enough to be read whole (PRESSED_CHOICE_MAX_LENGTH) and the text going out is exactly
 * the label (no reference rewrite or effort prefix on the way). The server checks the claim against Cleo's waiting card.
 */
export function pressedChoiceClaim(input: {
  readonly cardChoice: { readonly text: string; readonly card: RichReplyTappedCard } | undefined;
  readonly answeredWithAttachments: boolean;
  readonly outgoingText: string;
}): {
  readonly pressedChoice?: { assistantMessageId: string; replyIndex: number; cardId: string };
} {
  const choice = input.cardChoice;
  if (
    choice === undefined ||
    choice.card.pressed !== true ||
    input.answeredWithAttachments ||
    choice.text.length === 0 ||
    choice.text.length > PRESSED_CHOICE_MAX_LENGTH ||
    !pressedChoiceLabelPlain(choice.text) ||
    input.outgoingText !== choice.text
  )
    return {};
  return {
    pressedChoice: {
      assistantMessageId: choice.card.assistantMessageId,
      replyIndex: choice.card.replyIndex,
      cardId: choice.card.cardId,
    },
  };
}
